import { useMemo } from 'react';
import { Content, useContent } from 'src/contexts/ContentContext';
import { PosSelect } from 'src/core/POS/PosSelect';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Button, Popover, Stack } from 'src/core/ui';
import { CrossIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { NUMBER_RANGE_FILTER_OP_TO_CID } from 'src/utils/constants/contentIdMaps';
import { FilterOperationType } from 'src/WebApiController';

import { isUnaryRangeFilterOp, useMinFilterValue } from '../Filter.types';
import { StackedFilter } from '../StackedFilter';
import * as styles from './NumberRangeFilter.css';
import { NumberRangeFilterValue } from './NumberRangeFilter.types';

type NumberRangeFilterProps = {
  /**
   * Whether to enable the operation select.
   * If true, the operation select will be shown.
   * If false, the operation will be assumed to be between or greater than or less than
   */
  enableOperationSelect?: boolean;
  /**
   * Whether to layout the options horizontally or vertically - this affects the layout
   * when operationOptionsContent is provided.
   * Defaults to vertical.
   */
  isHorizontalLayout?: boolean;
  filterValue: NumberRangeFilterValue;
  setFilterValue: (value: NumberRangeFilterValue) => void;
  hideActions?: boolean;
};

export const NumberRangeFilter = ({
  enableOperationSelect = false,
  isHorizontalLayout = false,
  filterValue,
  setFilterValue,
  hideActions = false,
}: NumberRangeFilterProps) => {
  const minText = useContent(ContentId.Min);
  const maxText = useContent(ContentId.Max);
  const invalidMinMaxText = useContent(ContentId.InvalidMinMax);

  const needsTwoInputs = !isUnaryRangeFilterOp(filterValue.operation);

  // It is implied that when needsTwoInputs == false and useMinValueOnly == false, then useMaxValueOnly == true
  const useMinValueOnly = useMinFilterValue(filterValue.operation);

  const hasFilterValue = useMemo(() => {
    if (filterValue.operation === FilterOperationType.Between) {
      return filterValue.min != null && filterValue.max != null;
    }
    return filterValue.min != null || filterValue.max != null;
  }, [filterValue]);

  // When isInvalid is true, the UI shows an error message. On the backend,
  // CreateNumericRangeFilter also rejects inverted ranges (returns null),
  // so invalid ranges are excluded from the search request.
  const isInvalid =
    filterValue.min != null &&
    filterValue.max != null &&
    filterValue.min > filterValue.max;

  return (
    <StackedFilter
      isHorizontalLayout={isHorizontalLayout}
      renderSingleDirectly
      FilterComponents={[
        ...(enableOperationSelect
          ? [
              <PosSelect
                key="operationFilter"
                value={filterValue.operation ?? undefined}
                valueOptionsContent={NUMBER_RANGE_FILTER_OP_TO_CID}
                enableEmptySelection={false}
                onChange={(newOperation) => {
                  setFilterValue({
                    ...filterValue,
                    operation: newOperation as FilterOperationType,
                  });
                }}
              />,
            ]
          : []),
        <Stack
          key="numberRangeFilter"
          direction="column"
          className={styles.numberRangeFilter}
          gap="m"
          onClick={(e) => e.stopPropagation()}
        >
          {needsTwoInputs ? (
            <>
              <Stack direction="row" justifyContent="spaceBetween" gap="m">
                <PosTextField
                  type="number"
                  value={filterValue.min ?? ''}
                  onChange={(e) => {
                    const v = parseInt(e.target.value);
                    setFilterValue({
                      ...filterValue,
                      min: e.target.value ? v : undefined,
                    });
                  }}
                  placeholder={minText}
                  onClick={(e) => e.stopPropagation()}
                />
                <PosTextField
                  type="number"
                  value={filterValue.max ?? ''}
                  onChange={(e) => {
                    const v = parseInt(e.target.value);
                    setFilterValue({
                      ...filterValue,
                      max: e.target.value ? v : undefined,
                    });
                  }}
                  placeholder={maxText}
                  onClick={(e) => e.stopPropagation()}
                />
              </Stack>
              {isInvalid && (
                <span className={styles.validationMessage}>
                  {invalidMinMaxText}
                </span>
              )}
            </>
          ) : (
            <PosTextField
              type="number"
              value={
                (useMinValueOnly ? filterValue.min : filterValue.max) ?? ''
              }
              onChange={(e) => {
                const v = parseInt(e.target.value);

                setFilterValue({
                  ...filterValue,
                  [useMinValueOnly ? 'min' : 'max']: e.target.value
                    ? v
                    : undefined,
                  // clear the other value
                  [useMinValueOnly ? 'max' : 'min']: undefined,
                });
              }}
              onClick={(e) => e.stopPropagation()}
            />
          )}

          {!hideActions && (
            <Stack direction="row" justifyContent="spaceBetween">
              <Button
                variant="text"
                onClick={() => {
                  setFilterValue({
                    ...filterValue,
                    min: undefined,
                    max: undefined,
                  });
                }}
                disabled={!hasFilterValue}
              >
                <Content id={ContentId.ClearAll} />
              </Button>

              <Popover.Close asChild>
                <CrossIcon withHoverEffect />
              </Popover.Close>
            </Stack>
          )}
        </Stack>,
      ]}
    />
  );
};
